import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { MatchDateFormat } from 'src/app/_classes/match-date-format';
import { MatchFilters, ToggleDisplayModel, FilterModel, MatchData, MatchTypeValue } from 'src/app/_interfaces';
import { AppStringsService } from 'src/app/_services';

@Injectable({
    providedIn: 'root'
})
export class MatchTypeFilterService {
    public status_filters: FilterModel[] = [];
    public match_type_filters: FilterModel[] = [];
    public gender_filters: FilterModel[] = [];

    public match_data: BehaviorSubject<MatchData[]> = new BehaviorSubject<MatchData[]>([]);

    public match_filters_v2: BehaviorSubject<Map<MatchFilters, ToggleDisplayModel>> =
        new BehaviorSubject<Map<MatchFilters, ToggleDisplayModel>>(new Map());

    public reset_match_type_filters: Subject<boolean> = new Subject();

    public defualts: [MatchFilters, string][] = [
        ['date', new MatchDateFormat().formatDate()],
        ['status', 'all'],
        ['gender', 'all'],
        ['match_type', 'all']
    ];

    public active_filters: BehaviorSubject<Map<MatchFilters, MatchTypeValue>> =
        new BehaviorSubject<Map<MatchFilters, MatchTypeValue>>(
            new Map([
                ['date', new MatchDateFormat().formatDate()],
                ['status', 'all'],
                ['gender', 'all'],
                ['match_type', 'all']
            ])
        );

    constructor(
        private appStringAPI: AppStringsService
    ) {
        this.status_filters = [
            { label: this.appStringAPI.getAppString('tennis.matches.toggle.all'), value: 'all' },
            { label: this.appStringAPI.getAppString('tennis.matches.toggle.live'), value: 'playing' }
        ];

        this.match_type_filters = [
            { label: this.appStringAPI.getAppString('tennis.matches.toggle.all'), value: 'all' },
            { label: this.appStringAPI.getAppString('tennis.matches.toggle.type.singles'), value: 'singles' },
            { label: this.appStringAPI.getAppString('tennis.matches.toggle.type.doubles'), value: 'doubles' },
            { label: this.appStringAPI.getAppString('tennis.matches.toggle.type.qualifiers'), value: 'qualifiers' }
        ];

        this.gender_filters = [
            { label: this.appStringAPI.getAppString('tennis.matches.toggle.all'), value: 'all' },
            { label: this.appStringAPI.getAppString('tennis.matches.toggle.gender.men'), value: 'male' },
            { label: this.appStringAPI.getAppString('tennis.matches.toggle.gender.women'), value: 'female' }
        ];
    }

    private emitActiveFilters(): void {
        this.active_filters.next(new Map(this.active_filters.value));
    }

    private onlyQuals(types: string[]) {
        return types.every(type => ['RS', 'QS'].includes(type));
    }

    async buildFilters(matches: MatchData[], isDraws?: boolean) {
        if (isDraws) {
            const types = matches.reduce((acc: string[], match) => {
                if (!acc.includes(match.type)) {
                    acc.push(match.type);
                }
                return acc;
            }, []);

            if (this.active_filters.value.has('match_type') && (types.includes('MS') || types.includes('LS'))) {
                this.active_filters.value.set('match_type', 'singles');
            } else if (this.active_filters.value.has('match_type') && this.onlyQuals(types)) {
                this.active_filters.value.set('match_type', 'qualifiers');
            }

            const default_gender = types.includes('MS') ? 'male' : 'female';
            this.active_filters.value.set('gender', default_gender);

            if (!types.includes('MS') && !types.includes('LS')) {
                this.match_type_filters = this.match_type_filters.filter((f) => f.value !== 'singles');
            }
            if (!types.includes('MD') && !types.includes('LD')) {
                this.match_type_filters = this.match_type_filters.filter((f) => f.value !== 'doubles');
            }
            if (!types.includes('RS') && !types.includes('QS')) {
                this.match_type_filters = this.match_type_filters.filter((f) => f.value !== 'qualifiers');
            }

            const draws_filters: ToggleDisplayModel[] = [
                {
                    id: 'gender-filter',
                    type: 'gender',
                    filters: [...this.gender_filters.filter((f) => f.value !== 'all')]
                },
                {
                    id: 'match-type-filter',
                    type: 'match_type',
                    filters: [...this.match_type_filters.filter((f) => f.value !== 'all')]
                }
            ];

            const match_filter_map = new Map<MatchFilters, ToggleDisplayModel>();

            if (this.includeGender(matches)) {
                draws_filters.forEach((f) => {
                    match_filter_map.set(f.type as MatchFilters, f);
                });
            } else {
                draws_filters.forEach((f) => {
                    if (f.type !== 'gender') {
                        match_filter_map.set(f.type as MatchFilters, f);
                    }
                });
            }

            this.match_filters_v2.next(match_filter_map);
            return;
        }

        this.match_data.next(matches);

        const all_filters: ToggleDisplayModel[] = [
            {
                id: 'status-filter',
                type: 'status',
                filters: [...this.status_filters]
            },
            {
                id: 'gender-filter',
                type: 'gender',
                filters: [...this.gender_filters]
            },
            {
                id: 'match-type-filter',
                type: 'match_type',
                filters: [...this.match_type_filters]
            }
        ];

        const match_filter_map = new Map<MatchFilters, ToggleDisplayModel>();

        if (this.includeGender(matches)) {
            all_filters.forEach((f) => {
                match_filter_map.set(f.type as MatchFilters, f);
            });
        } else {
            all_filters.forEach((f) => {
                if (f.type !== 'gender') {
                    match_filter_map.set(f.type as MatchFilters, f);
                }
            });
        }

        this.match_filters_v2.next(match_filter_map);

        this.handleQualFilter();

        return;
    }

    includeGender(matches: MatchData[]): boolean {
        const hasLadies = matches.some((match) => match.team1.player1.gender === 'female');
        const hasGents = matches.some((match) => match.team1.player1.gender === 'male');
        return hasLadies && hasGents;
    }

    async updateFilter(group: MatchFilters, state: string, draws?: boolean) {
        if (this.active_filters.value.has(group)) {
            this.active_filters.value.set(group, state);
        }

        if (this.active_filters.value.size < this.defualts.length) {
            this.defualts.forEach(defualt => {
                if (!this.active_filters.value.has(defualt[0])) {
                    this.active_filters.value.set(defualt[0], defualt[1]);
                }
            });
        }

        this.emitActiveFilters();

        if (draws) {
            return;
        }

        this.handleQualFilter();
    }

    updateFilterState(filterMap: Map<MatchFilters, string>) {
        this.active_filters.next(new Map(filterMap));
    }

    getActiveFilters(): Map<MatchFilters, string> {
        return this.active_filters.value as any;
    }

    handleQualFilter() {
        const current_date_filter = this.active_filters.value.get('date');

        const today_matches = this.match_data.value.reduce((acc: MatchData[], m) => {
            if (new MatchDateFormat().formatDate(m.starts_at) === current_date_filter) {
                acc.push(m);
            }
            return acc;
        }, []);

        if (today_matches.length <= 0) {
            return;
        }

        const has_qual = today_matches.some((m) => ['RS', 'QS'].includes(m.type));
        const has_singles = today_matches.some((m) => ['MS', 'LS'].includes(m.type));

        const current_map = this.match_filters_v2.value;
        const current_match_filters = current_map.get('match_type');

        if (!current_match_filters) {
            return;
        }

        const qual_filter = {
            label: this.appStringAPI.getAppString('tennis.matches.toggle.type.qualifiers'),
            value: 'qualifiers'
        };

        const has_qual_filter = current_match_filters.filters?.some((f) => f.value === 'qualifiers');

        if (has_qual && !has_qual_filter) {
            current_match_filters.filters.push(qual_filter);
            current_map.set('match_type', current_match_filters);
            this.match_filters_v2.next(new Map(current_map));

            if (!has_singles && this.active_filters.value.get('match_type') !== 'qualifiers') {
                this.active_filters.value.set('match_type', 'qualifiers');
                this.emitActiveFilters();
                this.reset_match_type_filters.next(true);
            }
        } else if (!has_qual && has_qual_filter) {
            if (this.active_filters.value.get('match_type') === 'qualifiers') {
                this.active_filters.value.set('match_type', 'singles');
                this.emitActiveFilters();
                this.reset_match_type_filters.next(true);
            }

            current_match_filters.filters = current_match_filters.filters.filter((f) => f.value !== 'qualifiers');
            current_map.set('match_type', current_match_filters);
            this.match_filters_v2.next(new Map(current_map));
        }
    }
}
